import React, { useMemo } from 'react';

import get from 'lodash/get';

import {
  SidePage,
  Section,
  Actions,
  Action,
  SyncIcon,
} from '@gogame/components';
import { useChildComponent } from '@gogame/hooks-ts';
import Permissions from '@gogame/permissions';

import AudienceActions from 'scenes/CustomAudiences/Audiences/components/Actions';
import {
  AudienceFormEdit,
  AudienceFormDuplicate,
} from 'scenes/CustomAudiences/Audiences/components/AudienceForm';
import { FileBasedAudienceFormEdit } from 'scenes/CustomAudiences/Audiences/components/FileBasedAudienceForm';
import {
  ACTION_ADD_PARTNER_AUDIENCES,
  ACTION_SYNC_PARTNER_AUDIENCES,
  TYPE_AUDIENCE,
  TYPE_FILE_BASED_AUDIENCE,
} from 'scenes/CustomAudiences/Audiences/constants';
import {
  useIsUpdating,
  useLoadAudienceDetails,
  useLoadPartnerAudiencesAudience,
} from 'scenes/CustomAudiences/Audiences/data';
import {
  useSyncPartnerAudiencesAudience,
  useToggleAutoSyncAudience,
} from 'scenes/CustomAudiences/Audiences/data/mutations';
import {
  PartnerAudienceFormCreate,
  PARENT_AUDIENCE,
} from 'scenes/CustomAudiences/PartnerAudiences/components/PartnerAudienceForm';
import { TAB_DETAILS, TAB_ACTIONS_LOG } from 'scenes/CustomAudiences/constants';

import ActionsLog from './ActionsLog';
import Depositors from './Depositors';
import EngagementEvents from './EngagementEvents';
import PartnerAudiences from './PartnerAudiences';
import Activity from './Sections/Activity';
import FileBasedAudienceGeneral from './Sections/FileBasedAudienceGeneral';
import General from './Sections/General';
import Metadata from './Sections/Metadata';

const AudienceDetails = ({
  id,
  onClose,
  onExited,
  open,
  activeTabId = TAB_DETAILS,
}) => {
  const { data, isLoading, isRefetching } = useLoadAudienceDetails({
    id,
  });
  const { data: partnerData, isLoading: isPartnerAudienceLoading } =
    useLoadPartnerAudiencesAudience({ audience: id });
  const isUpdating = useIsUpdating();

  const [childComponent, openChildComponent] = useChildComponent();

  const [toggleAutoSync] = useToggleAutoSyncAudience();
  const [syncPartnerAudiencesAudience] = useSyncPartnerAudiencesAudience();

  const audienceTypeId = data?.type?.id;

  const handleEdit = ({ id }) => {
    let formEditComponent;

    switch (audienceTypeId) {
      case TYPE_AUDIENCE:
        formEditComponent = AudienceFormEdit;
        break;
      case TYPE_FILE_BASED_AUDIENCE:
        formEditComponent = FileBasedAudienceFormEdit;
        break;
      default:
        break;
    }

    openChildComponent(formEditComponent, {
      id,
    });
  };

  const handleDuplicate = async ({ id }) => {
    openChildComponent(AudienceFormDuplicate, {
      id,
    });
  };

  const handleToggleAutoSync = async () => {
    await toggleAutoSync({ id: data.id });
  };

  const handleSyncAll = async () => {
    await syncPartnerAudiencesAudience({
      audience: data?.id,
      name: data?.name,
      partnerAudiences: partnerData,
    });
  };

  const handleAdd = async () => {
    openChildComponent(PartnerAudienceFormCreate, {
      initialValues: { [PARENT_AUDIENCE]: data?.id },
    });
  };

  const partnerAudienceCount = useMemo(
    () => partnerData?.length || 0,
    [partnerData]
  );

  return (
    <SidePage
      backdrop={false}
      onClose={onClose}
      onExited={onExited}
      loading={isLoading || isUpdating}
      fetching={isRefetching || isPartnerAudienceLoading}
      open={open}
      size={'lg'}
    >
      <SidePage.Title
        title={'Audience'}
        subTitle={get(data, 'name')}
        entityId={id}
      />
      <SidePage.Tabs activeTabId={activeTabId}>
        <SidePage.Tab label={TAB_DETAILS}>
          <SidePage.NewBody wrapper={true} dense={true}>
            <SidePage.NewContent withSidebar={true}>
              {audienceTypeId === TYPE_AUDIENCE ? (
                <React.Fragment>
                  <Section title={'General'}>
                    <General data={data} />
                  </Section>
                  <Section
                    title={'Activity'}
                    isCollapsable={true}
                    initialOpen={true}
                  >
                    <Activity data={data} />
                  </Section>
                  {!!data?.depositor && (
                    <Section title={'Depositors'}>
                      <Depositors data={data} />
                    </Section>
                  )}
                  {!!data?.engagementEvents && (
                    <Section
                      title={'Engagement Events'}
                      isCollapsable
                      initialOpen={false}
                    >
                      <EngagementEvents data={data} />
                    </Section>
                  )}
                </React.Fragment>
              ) : null}
              {audienceTypeId === TYPE_FILE_BASED_AUDIENCE ? (
                <Section title={'General'}>
                  <FileBasedAudienceGeneral data={data} />
                </Section>
              ) : null}
              <Section
                title={`Partner Audiences (${partnerAudienceCount})`}
                isCollapsable={!!partnerAudienceCount}
                initialOpen={false}
                action={
                  <Permissions actions={data?.actions}>
                    {({ isActionAllowed }) => (
                      <Actions limit={2}>
                        <Action
                          icon={<SyncIcon />}
                          label={'Sync Partner audiences'}
                          disabled={
                            !isActionAllowed(ACTION_SYNC_PARTNER_AUDIENCES) ||
                            !partnerAudienceCount
                          }
                          onClick={handleSyncAll}
                        />
                        <Action
                          add
                          label={'Partner audiences'}
                          disabled={
                            !isActionAllowed(ACTION_ADD_PARTNER_AUDIENCES)
                          }
                          onClick={handleAdd}
                        />
                      </Actions>
                    )}
                  </Permissions>
                }
              >
                <PartnerAudiences data={partnerData} />
              </Section>
            </SidePage.NewContent>
            <SidePage.Sidebar
              actions={
                <AudienceActions
                  actions={data?.actions}
                  onEdit={handleEdit}
                  onDuplicate={handleDuplicate}
                  data={data}
                  onClose={onClose}
                  limit={3}
                  withPartnerActions={false}
                  onOpenChildComponent={openChildComponent}
                />
              }
            >
              <Metadata data={data} onToggle={handleToggleAutoSync} />
            </SidePage.Sidebar>
          </SidePage.NewBody>
        </SidePage.Tab>
        <SidePage.Tab label={TAB_ACTIONS_LOG}>
          <SidePage.NewBody scroll={false}>
            <ActionsLog id={id} />
          </SidePage.NewBody>
        </SidePage.Tab>
      </SidePage.Tabs>
      <SidePage.Actions>
        <SidePage.CloseButton />
      </SidePage.Actions>
      {childComponent}
    </SidePage>
  );
};

export default AudienceDetails;
